import { useTranslation } from 'react-i18next';
import { CustomModal } from '../../../components/CustomModal';
import { useCallback, useEffect, useMemo, useState } from 'react';
import API from '../../../service/api';
import { GroupTableView } from './GroupTableView';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { MDBBtn, MDBIcon } from 'mdb-react-ui-kit';
import { utils, writeFile } from 'xlsx';
import { OpGroupTableDTO } from '@scm/common-legacy';

export interface GroupTableViewModalProps {
    open: boolean;
    onClose: () => void;
    groupId: number;
    groupName: string;
}

export function GroupTableViewModal({
    open,
    onClose,
    groupId,
    groupName,
}: GroupTableViewModalProps) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<OpGroupTableDTO | null>(null);
    const [error, setError] = useState<Error | null>(null);

    const fetch = useCallback(() => {
        setData(null);
        setError(null);
        setLoading(true);
        API.GET(`/api/groups/${groupId}/table`)
            .then(setData)
            .catch(setError)
            .finally(() => {
                setLoading(false);
            });
    }, [groupId]);

    const refresh = useCallback(() => {
        fetch();
    }, [fetch]);

    const exportRows = useMemo(() => {
        if (!data) return [];

        const maxBoxes = data.locations.reduce((acc, location) => {
            const longestMachine = location.machines.reduce(
                (innerMax, machine) => Math.max(innerMax, machine.boxes.length),
                0,
            );
            return Math.max(acc, longestMachine);
        }, 0);

        const header = [
            t('location'),
            t('machine'),
            ...Array.from(
                { length: maxBoxes },
                (_, idx) => `${t('boxes')} (#${idx + 1})`,
            ),
        ];

        const rows = data.locations.flatMap((location) =>
            location.machines.map((machine) => {
                const boxValues = Array.from({ length: maxBoxes }, (_, idx) => {
                    const box = machine.boxes[idx];
                    if (!box) return '';
                    const qnty =
                        typeof box.qnty === 'number' ? box.qnty : undefined;
                    if (qnty === 0) return `${qnty} - ${t('status:BOX_EMPTY')}`;
                    if (
                        typeof qnty === 'number' &&
                        typeof machine.lowQuantityTarget === 'number' &&
                        qnty <= machine.lowQuantityTarget
                    )
                        return `${qnty} - ${t('status:BOX_LOW_QUANTITY')}`;
                    return qnty ?? '';
                });

                return [location.dto.Name, machine.dto.name, ...boxValues];
            }),
        );

        return rows.length > 0 ? [header, ...rows] : [];
    }, [data, t]);

    const getFileName = useCallback(() => {
        const now = new Date();
        const year = now.getFullYear().toString();
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        const day = now.getDate().toString().padStart(2, '0');
        const baseName = groupName
            .trim()
            .replace(/[^a-z0-9]+/gi, '_')
            .replace(/^_+|_+$/g, '')
            .toLowerCase();
        const prefix = baseName.length > 0 ? baseName : `group_${groupId}`;
        return `${prefix}_${year}_${month}_${day}`;
    }, [groupId, groupName]);

    const exportToExcel = useCallback(() => {
        if (exportRows.length === 0) return;
        const worksheet = utils.aoa_to_sheet(exportRows);
        const workbook = utils.book_new();
        utils.book_append_sheet(workbook, worksheet, 'Sheet1');
        writeFile(workbook, `${getFileName()}.xlsx`);
    }, [exportRows, getFileName]);

    const exportToCSV = useCallback(() => {
        if (exportRows.length === 0) return;
        const worksheet = utils.aoa_to_sheet(exportRows);
        const csv = utils.sheet_to_csv(worksheet);
        const blob = new Blob([csv], { type: 'text/plain' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${getFileName()}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    }, [exportRows, getFileName]);

    const canExport = exportRows.length > 0;

    useEffect(() => {
        if (!open) return;
        fetch();
    }, [fetch, open]);

    return (
        <CustomModal
            title={groupName}
            open={open}
            onClose={onClose}
            dialogStyle={{
                width: '100vw',
                maxWidth: 1200,
                paddingLeft: 10,
                paddingRight: 10,
            }}
            bodyStyle={{ height: '85vh' }}
            header={
                <div className="d-flex flex-row align-items-center">
                    <MDBBtn
                        color="secondary"
                        className="py-2 ms-3 d-flex flex-row align-items-center"
                        onClick={refresh}
                        disabled={loading}
                    >
                        <span className={`me-1${loading ? ' d-none' : ''}`}>
                            <MDBIcon icon="sync" />
                        </span>
                        <span className={`me-1${!loading ? ' d-none' : ''}`}>
                            <LoadingSpinner size="sm" className="p-0" />
                        </span>
                        <span>{t('reset')}</span>
                    </MDBBtn>
                    <MDBBtn
                        color="secondary"
                        className="py-2 ms-2 d-flex flex-row align-items-center"
                        onClick={exportToCSV}
                        disabled={!canExport}
                    >
                        <span className="me-1">
                            <MDBIcon icon="file-csv" />
                        </span>
                        <span>{t('export_to_csv')}</span>
                    </MDBBtn>
                    <MDBBtn
                        color="secondary"
                        className="py-2 ms-2 d-flex flex-row align-items-center"
                        onClick={exportToExcel}
                        disabled={!canExport}
                    >
                        <span className="me-1">
                            <MDBIcon icon="file-excel" />
                        </span>
                        <span>{t('export_to_excel')}</span>
                    </MDBBtn>
                </div>
            }
        >
            {!data && <LoadingSpinner />}

            {data && <GroupTableView groupId={groupId} table={data} />}
        </CustomModal>
    );
}
